/* eslint-disable @typescript-eslint/ban-ts-comment */
import { UserInfo } from "@foundation/reg-ui-client";

import { isClient } from "~utils/isClient";

const MOBILE_BREAKPOINT = "(max-width:767px)";

// Below util functions extracted from javelin/views/foundation/ShareArticle/ShareArticle.module.jsx

export const toEncodedQueryParams = (params: {
    [key: string]: string;
}): string =>
    Object.keys(params).reduce((accumulator, currentKey) => {
        const key = currentKey;
        const value = encodeURIComponent(params[key]);
        const param = `${key}=${value}`;

        if (!accumulator) {
            return param;
        }

        return `${accumulator}&${param}`;
    }, "");

export const decorateCanonicalWithUTMParams = (
    canonical: string,
    campaign: string
) => {
    const queryParams = toEncodedQueryParams({
        utm_source: "website",
        utm_medium: "share",
        utm_campaign: campaign,
    });

    return `${canonical}?${queryParams}`;
};

export const handleSocialClick = (
    href: string,
    action: string,
    label: string,
    event: Event,
    userInfo: UserInfo | null
) => {
    if (!isClient()) return;

    event.preventDefault();

    window.dataLayer?.push({
        event: "article_social_share",
        eventCategory: "social",
        eventAction: action,
        eventLabel: label,
    });

    // @ts-ignore the userInfo.user type exposed from @foundation/reg-ui-client does not have the property referralToken defined yet
    const referralToken = userInfo?.user?.referralToken;

    const linkUrl = referralToken
        ? `${href}${encodeURIComponent(`?sref=${referralToken}`)}`
        : href;

    const mobileMediaQuery = window.matchMedia(MOBILE_BREAKPOINT);

    if (mobileMediaQuery.matches) {
        window.open(linkUrl, "_self");

        return;
    }

    const customWindowParams =
        "menubar=no,toolbar=no,resizable=yes,scrollbars=yes,width=626,height=438";

    window.open(linkUrl, "", customWindowParams);
};

export const handleCopyClick = (url: string, label: string) => {
    if (!isClient()) return;

    window.dataLayer?.push({
        event: "article_social_share",
        eventCategory: "social",
        eventAction: "share",
        eventLabel: label,
    });

    // navigator clipboard api needs a secure context (eg. https)
    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url);
    }
};

export const facebookShareHandler = ({
    canonical,
    event,
    userInfo,
}: {
    canonical: string;
    event: Event;
    userInfo: UserInfo | null;
}) => {
    const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
        canonical,
        "facebook"
    );

    const queryParams = toEncodedQueryParams({
        u: canonicalWithUTMParams,
    });

    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?${queryParams}`;

    return handleSocialClick(
        facebookShareUrl,
        "share",
        "facebook",
        event,
        userInfo
    );
};

export const xShareHandler = ({
    canonical,
    twitterHandle,
    twitterText,
    event,
    userInfo,
}: {
    canonical: string;
    twitterHandle: string;
    twitterText: string;
    event: Event;
    userInfo: UserInfo | null;
}) => {
    const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
        canonical,
        "twitter"
    );

    // Note - twitter auto adds '@' prefix to handles here, so be sure to remove.
    const queryParams = toEncodedQueryParams({
        text: (twitterHandle && twitterText) || "",
        url: canonicalWithUTMParams,
    });

    const xShareUrl = `https://x.com/intent/tweet?${queryParams}`;

    return handleSocialClick(xShareUrl, "share", "x", event, userInfo);
};

export const linkedInShareHandler = ({
    canonical,
    headline,
    summary = "",
    event,
    userInfo,
}: {
    canonical: string;
    headline: string;
    summary?: string;
    event: Event;
    userInfo: UserInfo | null;
}) => {
    const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
        canonical,
        "linkedin"
    );

    const queryParams = toEncodedQueryParams({
        mini: "true",
        title: headline,
        summary,
        url: canonicalWithUTMParams,
    });

    const linkedInShareUrl = `https://www.linkedin.com/shareArticle?${queryParams}`;

    return handleSocialClick(
        linkedInShareUrl,
        "share",
        "linkedin",
        event,
        userInfo
    );
};

export const emailShareHandler = ({
    byline,
    canonical,
    headline,
    summary = "",
    event,
    userInfo,
}: {
    byline: string;
    canonical: string;
    headline: string;
    summary?: string;
    event: Event;
    userInfo: UserInfo | null;
}) => {
    const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
        canonical,
        "email"
    );

    const queryParams = toEncodedQueryParams({
        subject: `${headline} - Bloomberg`,
        body: `${headline}\r\n\r\nby ${byline}\r\n\r\n${summary}\r\n\r\n${canonicalWithUTMParams}`,
    });

    const href = `mailto:?${queryParams}`;

    return handleSocialClick(href, "share", "email", event, userInfo);
};

export const copyLinkHandler = ({ canonical }: { canonical: string }) => {
    const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
        canonical,
        "copy"
    );

    return handleCopyClick(canonicalWithUTMParams, "link");
};
